import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { serverMessage } from "@/lib/apiError";
import {
  getMyFiscalDeclaration,
  saveMyFiscalDeclaration,
} from "@/services/professionalEarningsService";

/**
 * The declaration form, shown INLINE when the server refuses a quote for want
 * of fiscal data.
 *
 * THIS COMPONENT IS WHAT MAKES THE GATE A GATE RATHER THAN AN OUTAGE. No
 * professional on the platform currently holds a RUT, so every one of them
 * meets that refusal on their next quote. Blocking the send is only defensible
 * because the remedy appears in the same interaction: they fill this in once
 * and send. Removing it does not degrade the feature, it stops the
 * marketplace.
 *
 * Copy comes from the shared `auth.professionalFiscal` namespace — the same
 * strings both onboarding flows use — because this is the same question asked
 * at a different moment, and two wordings would drift.
 */
export function FiscalDeclarationPrompt({
  onSaved,
  submitLabel,
  variant = "card",
}: {
  /**
   * "card" keeps the bordered panel this started as, for use inline on a page.
   * "plain" drops it, for a dialog — the dialog IS the container, and a panel
   * inside a panel reads as a form that lost its frame.
   */
  variant?: "card" | "plain";
  /** Called after a successful save, so the caller can retry the send. */
  onSaved: () => void;
  /**
   * Overrides the save button's label.
   *
   * The default, "Guardar y enviar presupuesto", is true where this prompt
   * first appeared — interrupting a professional mid-quote, where saving
   * retries the send they just attempted. It is FALSE on an already-awarded
   * job: that professional sent their quote days ago and won it, so a button
   * offering to send it again describes work that is behind them. The CFE gate
   * passes its own label.
   */
  submitLabel?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const [rut, setRut] = useState("");
  const [razonSocial, setRazonSocial] = useState("");
  const [issuesCfe, setIssuesCfe] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  /**
   * PREFILLED from whatever is already on file.
   *
   * Most professionals have nothing and see an empty form, but someone who
   * declared partially — or is correcting a typo that is blocking their quote
   * — should not retype what the platform already holds. Failure is silent on
   * purpose: this is a convenience, and an unreachable GET must not stop
   * someone from filling the form by hand.
   */
  useEffect(() => {
    let cancelled = false;
    void getMyFiscalDeclaration()
      .then((state) => {
        if (cancelled) return;
        if (state.rut) setRut(state.rut);
        if (state.razonSocial) setRazonSocial(state.razonSocial);
        if (state.issuesCfe !== null) setIssuesCfe(state.issuesCfe);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * A "no" IS SAVED, it just cannot send.
   *
   * An earlier version required `issuesCfe === true` to save at all, which
   * meant a professional who answered "no" saw the offer of help on screen
   * and the platform never learned it — contradicting this component's own
   * copy and the onboarding design, where a false answer is deliberately
   * captured so the people who need help can be found. Persisting it is the
   * whole point of asking.
   */
  const canSave =
    rut.length === 12 && razonSocial.trim().length >= 2 && issuesCfe !== null;

  const save = async (): Promise<void> => {
    if (!canSave || issuesCfe === null) return;
    setSaving(true);
    try {
      await saveMyFiscalDeclaration({
        rut,
        razonSocial: razonSocial.trim(),
        issuesCfe,
      });
      // Only a "yes" unblocks the quote. A "no" is recorded and the
      // professional stays here with the offer of help — retrying the send
      // would just earn the same refusal.
      if (issuesCfe) onSaved();
    } catch (err) {
      toast.error(serverMessage(err) ?? t("auth.professionalFiscal.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const panelClass =
    variant === "plain"
      ? "space-y-4"
      : "space-y-4 rounded-xl border border-border bg-muted/30 p-5 dark:bg-muted/20";

  return (
    <div className="space-y-4">
      <div className={panelClass}>
      {/* NO TITLE OR REASON OF ITS OWN. This used to render
          `sectionTitle` + `promptReason` here, and every one of the three call
          sites wraps it in a container that ALREADY has a heading — so all
          three showed the heading twice. In the dialog it was literally the
          same two strings stacked on themselves (the DialogHeader renders that
          exact pair); the profile card repeated its own CardTitle; the
          consumer CFE gate stacked a second title under its h2.

          The WHY still has to be said — this can interrupt someone mid-quote —
          but it belongs to whoever opened the form, who is the only one that
          knows which interruption this is. The dialog says it in its
          DialogDescription, the CFE gate in `fiscalFirstLead`, and the profile
          card needs no reason at all because the professional pressed Editar
          on their own invoicing details. */}
      <div className="grid gap-2">
        <Label htmlFor="prompt-fiscal-rut">
          {t("auth.professionalFiscal.rutLabel")}
        </Label>
        <Input
          id="prompt-fiscal-rut"
          inputMode="numeric"
          autoComplete="off"
          maxLength={12}
          placeholder={t("auth.professionalFiscal.rutPlaceholder")}
          value={rut}
          onChange={(e) => setRut(e.target.value.replace(/\D/g, ""))}
          disabled={saving}
        />
      </div>

      <div className="grid gap-2">
        <Label htmlFor="prompt-fiscal-razon-social">
          {t("auth.professionalFiscal.razonSocialLabel")}
        </Label>
        <Input
          id="prompt-fiscal-razon-social"
          autoComplete="organization"
          maxLength={255}
          placeholder={t("auth.professionalFiscal.razonSocialPlaceholder")}
          value={razonSocial}
          onChange={(e) => setRazonSocial(e.target.value)}
          disabled={saving}
        />
        <p className="text-xs text-muted-foreground">
          {t("auth.professionalFiscal.razonSocialHelp")}
        </p>
      </div>

      <div className="grid gap-2">
        <Label>{t("auth.professionalFiscal.cfeLabel")}</Label>
        {/* A CHOICE, not two actions. As plain side-by-side buttons these read
            as "do this" / "do that" — a professional cannot tell that picking
            one is required and that one of them is already picked. Radio
            semantics plus a visible selected state says both. */}
        <div role="radiogroup" className="grid gap-2 sm:grid-cols-2">
          <CfeChoice
            label={t("auth.professionalFiscal.cfeYes")}
            selected={issuesCfe === true}
            disabled={saving}
            onSelect={() => setIssuesCfe(true)}
          />
          <CfeChoice
            label={t("auth.professionalFiscal.cfeNo")}
            selected={issuesCfe === false}
            disabled={saving}
            onSelect={() => setIssuesCfe(false)}
          />
        </div>
      </div>

      {issuesCfe === false ? (
        // A "no" is a real answer and is kept on screen — but it cannot send a
        // quote, because under the invoicing model the professional is the
        // party that invoices the consumer. Saying so here, with the offer of
        // help, beats a disabled button with no explanation.
        <div className="rounded-lg border border-border bg-background p-4 text-sm">
          <p className="font-medium text-foreground">
            {t("auth.professionalFiscal.noCfeTitle")}
          </p>
          {/* A REAL LINK, not the word "Fiscalo" sitting in prose. Someone who
              answers "todavía no" is being told they cannot work here yet; the
              one useful thing on this screen is the way out, and it was not
              clickable. Opens in a new tab so the half-filled declaration
              behind it survives. */}
          <p className="mt-1 text-muted-foreground">
            {t("auth.professionalFiscal.noCfeBodyBefore")}
            <a
              href="https://fiscalo.uy"
              target="_blank"
              rel="noreferrer noopener"
              className="font-medium text-primary underline underline-offset-2 hover:no-underline"
            >
              {t("auth.professionalFiscal.noCfeBodyLink")}
            </a>
            {t("auth.professionalFiscal.noCfeBodyAfter")}
          </p>
        </div>
      ) : null}

      </div>

      {/* OUTSIDE THE CARD, so the action reads as the step's footer rather than
          as one more field inside the form. It was the last child of the
          bordered panel, which put the commit alongside the inputs it commits. */}
      <div className="flex justify-end">
        <Button type="button" onClick={() => void save()} disabled={!canSave || saving}>
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("auth.professionalFiscal.saving")}
            </>
          ) : issuesCfe === false ? (
            // Honest label: this saves the answer, it does not send the quote.
            t("auth.professionalFiscal.save")
          ) : (
            (submitLabel ?? t("auth.professionalFiscal.saveAndSend"))
          )}
        </Button>
      </div>
    </div>
  );
}

function CfeChoice({
  label,
  selected,
  disabled,
  onSelect,
}: {
  label: string;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "flex items-center gap-2.5 rounded-lg border px-3.5 py-3 text-left text-sm transition-colors duration-150",
        "disabled:cursor-not-allowed disabled:opacity-60",
        selected
          ? "border-primary bg-primary/5 font-medium text-foreground"
          : "border-border bg-background text-muted-foreground hover:border-foreground/30 hover:text-foreground",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "flex h-4 w-4 shrink-0 items-center justify-center rounded-full border",
          selected ? "border-primary" : "border-muted-foreground/50",
        )}
      >
        {selected ? <span className="h-2 w-2 rounded-full bg-primary" /> : null}
      </span>
      {label}
    </button>
  );
}
